<script setup lang="ts">
import { AppLayout } from '@app/customer/Layouts';
import { router } from '@inertiajs/vue3';
import { PollPaymentPageViewModel } from './types';
import { onMounted, onUnmounted } from 'vue';
import { useBrandColor } from '@app/customer/Composables/useBrandColor';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<PollPaymentPageViewModel>();

let timeoutProcessId: NodeJS.Timeout|null = null;

const spinnerColor = useBrandColor();

onMounted(() => {
  timeoutProcessId = setTimeout(poll, props.pollAfterMs);
});

onUnmounted(() => {
  if (timeoutProcessId !== null) {
    clearTimeout(timeoutProcessId);

    // Forcefully stop timeout process
    timeoutProcessId = null;
  }
});

function poll() {
  router.get(route('customer.payment-transaction.validate', { paymentTransaction: props.paymentTransactionId }), {}, {
    preserveState: true,
    replace: true,
    onFinish: () => {
      // Check if timeout process has been stopped forcefully
      if (timeoutProcessId === null) {
        return;
      }

      timeoutProcessId = setTimeout(poll, props.pollAfterMs);
    },
  });
}
</script>

<template>
  <AppLayout>
    <div class="mx-auto mt-10 grid max-w-screen-md justify-items-center rounded-lg border border-slate-200 py-12 px-4 text-center">
      <svg
        class="size-12 sm:size-14 animate-spin"
        viewBox="0 0 66 66"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M62 33C62 36.8083 61.2499 40.5794 59.7925 44.0978C58.3351 47.6163 56.199 50.8132 53.5061 53.5061C50.8132 56.199 47.6163 58.3351 44.0978 59.7925C40.5794 61.2499 36.8083 62 33 62C29.1917 62 25.4206 61.2499 21.9022 59.7925C18.3837 58.3351 15.1868 56.199 12.4939 53.5061C9.801 50.8132 7.66488 47.6163 6.20749 44.0978C4.7501 40.5794 4 36.8083 4 33C4 29.1917 4.75011 25.4206 6.2075 21.9022C7.66489 18.3837 9.80101 15.1868 12.4939 12.4939C15.1868 9.801 18.3837 7.66487 21.9022 6.20749C25.4206 4.7501 29.1917 4 33 4C36.8083 4 40.5794 4.75011 44.0978 6.2075C47.6163 7.66489 50.8132 9.80101 53.5061 12.4939C56.199 15.1868 58.3351 18.3838 59.7925 21.9022C61.2499 25.4206 62 29.1917 62 33L62 33Z"
          stroke="#F1F5F9"
          stroke-width="8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M33 4C36.8083 4 40.5794 4.75011 44.0978 6.20749C47.6163 7.66488 50.8132 9.80101 53.5061 12.4939C56.199 15.1868 58.3351 18.3837 59.7925 21.9022C61.2499 25.4206 62 29.1917 62 33"
          :stroke="spinnerColor.background"
          stroke-width="8"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <h1 class="pt-8 text-2xl sm:text-3xl font-semibold">
        {{ t('customer.poll-payment:title') }}
      </h1>
      <p class="pt-2 text-slate-500">
        {{ t('customer.poll-payment:content') }}
      </p>
    </div>
  </AppLayout>
</template>
